import React, { useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { useDrag, useDrop } from "react-dnd";
import { DragHandle, VisibilityOff } from "@mui/icons-material";
import { ItemTypes } from "../helpers/enums";
import {
  SET_ACTIVE_ELEMENT,
  useScrollytellDispatch,
  useScrollyTellProps,
  useScrollytellTranslation,
} from "./scrollytellContext";
import {
  deleteSection,
  updateSection,
  updateSectionOrder,
  insertSection,
} from "./service";
import { Section } from "../Section/Section";
import { EditSectionTitle } from "./EditSectionTitle";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { MiniSectionMenu } from "./MiniSectionMenu";
import { getSectionDisplayTitle, getSectionTemplateBadge } from "./sidebarHelpers";

const useStyles = createUseStyles({
  card: {
    display: "flex",
    flexDirection: "column",
    width: "calc(100% - 24px)",
    boxSizing: "border-box",
    margin: "0 auto",
    borderRadius: "8px",
    overflow: "hidden",
    cursor: "pointer",
    transition: "all 150ms ease-out",
    backgroundColor: "#1E293B",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    opacity: ({ isDragging }) => (isDragging ? 0.4 : 1),
    "&.active": {
      border: "2px solid #4484F1",
      borderLeft: "4px solid #4484F1",
      backgroundColor: "#0F172A",
      boxShadow: "0 4px 12px rgba(68, 132, 241, 0.25)",
    },
    "&:hover": { borderColor: "#4484F1" },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 8px",
    gap: "6px",
    boxSizing: "border-box",
  },
  leftGroup: { display: "flex", alignItems: "center", gap: "6px", overflow: "hidden", flex: 1 },
  titleText: {
    color: "#FFFFFF",
    fontSize: "12px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rightGroup: { display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 },
  badge: {
    fontSize: "9.5px",
    fontWeight: 700,
    padding: "2px 6px",
    borderRadius: "4px",
    backgroundColor: "#334155",
    color: "#93C5FD",
    letterSpacing: "0.3px",
  },
  previewContainer: {
    height: "96px",
    width: "100%",
    overflow: "hidden",
    position: "relative",
    backgroundColor: "#0F172A",
    borderTop: "1px solid rgba(255, 255, 255, 0.08)",
    "& *": { pointerEvents: "none" },
  },
});

export const MiniSection = ({
  index,
  section,
  currentSection,
  totalSections,
  compactMode = false,
  onInsertAt,
}) => {
  const t = useScrollytellTranslation();
  const { onChange, story } = useScrollyTellProps();
  const dispatch = useScrollytellDispatch();
  const cardRef = useRef(null);
  const dragRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [titleEditOpen, setTitleEditOpen] = useState(false);

  const isActive = currentSection === index;

  useEffect(() => {
    if (isActive && cardRef.current) {
      cardRef.current.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    }
  }, [isActive]);

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.SECTION,
    item: () => ({ order: section.order, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.SECTION,
    hover(item, monitor) {
      if (!cardRef.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = cardRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      onChange(updateSectionOrder(story, dragIndex, hoverIndex));
      item.index = hoverIndex;
    },
  });

  const classes = useStyles({ isDragging });
  preview(drop(cardRef));
  drag(dragRef);

  const scrollTo = () => {
    dispatch?.({
      type: SET_ACTIVE_ELEMENT,
      activeSection: index,
      activeContainer: undefined,
      activeBlock: undefined,
    });
    const el = document.getElementById(`section-${index + 1}`) || document.getElementById(`section-${section.order}`);
    el?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const displayTitle = getSectionDisplayTitle(section, index, t);
  const badge = getSectionTemplateBadge(section, t);

  return (
    <div
      ref={cardRef}
      className={`${classes.card} ${isActive ? "active" : ""}`}
      onClick={scrollTo}
      data-testid={`mini-section-${index}`}
    >
      <div className={classes.header}>
        <div className={classes.leftGroup}>
          <div
            ref={dragRef}
            style={{ cursor: "grab", display: "flex", alignItems: "center", color: "#94A3B8" }}
            onClick={(e) => e.stopPropagation()}
            title={t("reorderSectionTooltip", "Dra for å endre rekkefølge")}
            data-testid={`drag-handle-${index}`}
          >
            <DragHandle fontSize="small" sx={{ fontSize: 18 }} />
          </div>
          <span className={classes.titleText} title={displayTitle}>
            {index + 1}. {displayTitle}
          </span>
        </div>

        <div className={classes.rightGroup}>
          {section.excludeFromNav && (
            <VisibilityOff sx={{ fontSize: 14, color: "#FCD34D" }} titleAccess={t("hiddenFromToc", "Skjult fra innholdsfortegnelse")} />
          )}
          <span className={classes.badge}>{badge}</span>
          <MiniSectionMenu
            index={index}
            totalSections={totalSections}
            excludeFromNav={Boolean(section.excludeFromNav)}
            onEditTitle={() => setTitleEditOpen(true)}
            onInsertAbove={() => onInsertAt?.(index)}
            onInsertBelow={() => onInsertAt?.(index + 1)}
            onMoveUp={() => onChange(updateSectionOrder(story, index, index - 1))}
            onMoveDown={() => onChange(updateSectionOrder(story, index, index + 1))}
            onToggleNav={() => onChange(updateSection(story, index, { ...section, excludeFromNav: !section.excludeFromNav }))}
            onCopy={() => onChange(insertSection(story, { ...section }, index + 1))}
            onDelete={() => setConfirmOpen(true)}
          />
        </div>
      </div>

      {!compactMode && (
        <div className={classes.previewContainer}>
          <Section
            isAdminMode={false}
            section={section}
            sectionId={index}
            isThumbnail={true}
            style={{
              transform: "scale(0.30)",
              transformOrigin: "left top",
              minHeight: "320px",
              width: "700px",
            }}
          />
        </div>
      )}

      {titleEditOpen && (
        <EditSectionTitle
          value={section.title}
          isOpen={titleEditOpen}
          onClose={() => setTitleEditOpen(false)}
          onChange={(newTitle) => {
            setTitleEditOpen(false);
            onChange(updateSection(story, index, { ...section, title: newTitle }));
          }}
        />
      )}

      <DeleteConfirmDialog
        open={confirmOpen}
        title={section.title || t("a11yInspectorDrawer.sectionLabel", `Seksjon ${index + 1}`, { section: index + 1 })}
        containerCount={(section.containers || []).length}
        blockCount={(section.containers || []).reduce((acc, c) => acc + (c.blocks || []).length, 0)}
        onConfirm={() => {
          setConfirmOpen(false);
          onChange(deleteSection(story, index));
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};
